import type { useSearchPharmaciesQuery } from '@/entities/rx';

/** Minimum length before name, city or ZIP counts as something to search for. */
export const PHARMACY_SEARCH_MIN_LENGTH = 3;

/**
 * DoseSpot's pharmacy search can return an empty page when no specialty filter
 * is set; `Any` means "do not filter by specialty". The patient pharmacy search
 * sends it for the same reason.
 */
const ANY_PHARMACY_SPECIALTY = 'Any';

export type PharmacySearchTextFields = Readonly<{
  name: string;
  city: string;
  state: string;
  zip: string;
}>;

export type PharmacySearchInput = NonNullable<Parameters<typeof useSearchPharmaciesQuery>[0]>;

/**
 * The pharmacy search to run for what the picker's boxes hold, or undefined
 * when there is nothing to search for yet.
 *
 * Any one of name, city or ZIP at the minimum length is enough; each is sent
 * only once it reaches that length. State narrows a search but is never one on
 * its own, and is sent only as a two-letter code.
 */
export function buildPharmacySearchInput(
  fields: PharmacySearchTextFields
): PharmacySearchInput | undefined {
  const name = fields.name.trim();
  const city = fields.city.trim();
  const state = fields.state.trim().toUpperCase();
  const zip = fields.zip.trim();

  const usable = (value: string) => value.length >= PHARMACY_SEARCH_MIN_LENGTH;
  if (!usable(name) && !usable(city) && !usable(zip)) {
    return undefined;
  }

  return {
    name: usable(name) ? name : undefined,
    city: usable(city) ? city : null,
    state: state.length === 2 ? state : null,
    zip: usable(zip) ? zip : null,
    specialty: [ANY_PHARMACY_SPECIALTY],
  };
}

/** What the State box keeps of what is typed: at most two letters, upper-cased. */
export function normalizeStateInput(value: string): string {
  return value
    .replace(/[^a-z]/gi, '')
    .slice(0, 2)
    .toUpperCase();
}
